import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { TagsDisplay } from "components/TagsDisplay";
import { FacilityAlternateIdKnownKind } from "encamp-shared/src/facility/alternateId";
import { findAlternateId } from "encamp-shared/src/facilityAlternateId/findAlternateId";
import { formatAddress } from "encamp-shared/src/utils/address";
import { FacilityWasteView } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { Suspense, useEffect, useMemo, useState } from "react";
import { formatShipmentRangeDate } from "../../formatShipmentRangeDate";
import { useWasteFacility } from "../../useWasteFacility";
import { MergedWasteStreamChip } from "../MergedWasteStreamChip";
import { TileContentGreyBackground } from "../TileContentGreyBackground";
import { useWasteFacilityState } from "../WasteFacilityContext";
import { WasteStreamUnmergeDialog } from "../WasteStreamsDataGrid/WasteStreamMerge/WasteStreamUnmergeDialog";
import { useWasteStream } from "./api";
import { DestinationsDataGrid } from "./Destinations/DestinationsDataGrid";
import { EditWasteStreamDialog } from "./EditWasteStreamDialog";
import { ManifestLineItemsDataGrid } from "./ManifestLineItemsDataGrid";
import { WasteStreamInfoSection } from "./WasteStreamInfoSection";
import { WasteStreamShipmentsByMonth } from "./WasteStreamShipmentsByMonth";

interface WasteStreamDetailsModalProps {
  open: boolean;
  onClose: () => void;
  wasteStreamId: string;
}

const getReadonlyWasteViewText = (view: FacilityWasteView) => {
  switch (view) {
    case FacilityWasteView.All:
      return "Displaying all waste";
    case FacilityWasteView.Reportable: {
      return "Displaying only reportable waste";
    }
    case FacilityWasteView.NotReportable: {
      return "Displaying only non-reportable waste";
    }
  }
};

export type ManifestType = "vendor" | "e‑Manifest";

// Single loading skeleton for the entire modal
const WasteStreamDetailsModalSkeleton = () => (
  <Box sx={{ p: 3 }}>
    <Stack gap={3}>
      {/* Header skeleton */}
      <Box>
        <Skeleton variant="text" width={300} height={40} />
        <Skeleton variant="text" width={200} height={20} sx={{ mt: 1 }} />
      </Box>

      {/* Toggle buttons skeleton */}
      <Box sx={{ display: "flex", gap: 1 }}>
        <Skeleton variant="rectangular" width={80} height={36} />
        <Skeleton variant="rectangular" width={100} height={36} />
      </Box>

      {/* Content skeleton */}
      <Skeleton variant="rectangular" height={200} width="100%" />
      <Skeleton variant="rectangular" height={200} width="100%" />
      <Skeleton variant="rectangular" height={50} width="100%" />
      <Skeleton variant="rectangular" height={150} width="100%" />
      <Skeleton variant="rectangular" height={150} width="100%" />
    </Stack>
  </Box>
);

// Main content component that gets wrapped in Suspense
const WasteStreamDetailsContent = ({
  wasteStreamId,
  onClose,
}: {
  wasteStreamId: string;
  onClose: () => void;
}) => {
  const {
    wasteFacilityState: { deferredFilters },
    effectiveWasteFacilityId,
  } = useWasteFacilityState();
  const { data: facilityData } = useWasteFacility(effectiveWasteFacilityId);
  const { data: wasteStreamData } = useWasteStream(wasteStreamId);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [unmergeDialogOpen, setUnmergeDialogOpen] = useState(false);

  // Controls what manifest type is displayed by default
  const manifestState = useMemo((): {
    hasVendorData: boolean;
    hasEManifestData: boolean;
    defaultType: ManifestType;
  } => {
    const vendorCount = wasteStreamData?.wasteStream?.vendorManifestCount ?? 0;
    const eManifestCount = wasteStreamData?.wasteStream?.eManifestCount ?? 0;

    return {
      hasVendorData: vendorCount > 0,
      hasEManifestData: eManifestCount > 0,
      defaultType:
        vendorCount === 0 && eManifestCount > 0 ? "e‑Manifest" : "vendor",
    };
  }, [
    wasteStreamData?.wasteStream?.vendorManifestCount,
    wasteStreamData?.wasteStream?.eManifestCount,
  ]);

  const [manifestType, setManifestType] = useState<ManifestType>(
    manifestState.defaultType
  );

  // Update manifestType when default changes (when data loads)
  useEffect(() => {
    setManifestType(manifestState.defaultType);
  }, [manifestState.defaultType]);

  const hasNoManifestData =
    manifestType === "vendor"
      ? !manifestState.hasVendorData
      : !manifestState.hasEManifestData;

  const facility = facilityData?.facility;

  // Get facility alternate IDs
  const epaId = findAlternateId(facility?.facilityAlternateIds ?? [], [
    FacilityAlternateIdKnownKind.EPA,
  ])?.value;

  const { startDate, endDate, view, tags } = deferredFilters;
  const formattedDateRange = useMemo(() => {
    return formatShipmentRangeDate({
      startDate,
      endDate,
    });
  }, [startDate, endDate]);

  // Check if waste stream is merged
  const isMerged = (wasteStreamData?.wasteStream?.mergeCount ?? 0) > 1;

  const handleEditClick = () => {
    if (isMerged) {
      setUnmergeDialogOpen(true);
    } else {
      setEditDialogOpen(true);
    }
  };

  const handleManifestToggle = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: ManifestType
  ) => {
    if (newValue !== null) {
      setManifestType(newValue);
    }
  };

  return (
    <>
      {/* White Content Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          backgroundColor: "background.paper",
          position: "sticky",
          top: 0,
          zIndex: 2,
        }}
      >
        {/* Title Row with Edit Icon and Action Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <WasteStreamDetailTitle
            wasteStreamId={wasteStreamId}
            onEditClick={handleEditClick}
          />
          {/* Date Range and Action Buttons */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexShrink={0}
            sx={{ pl: 5 }}
          >
            <Stack spacing={0.5}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ wordBreak: "break-word" }}
              >
                {formattedDateRange}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getReadonlyWasteViewText(view ?? FacilityWasteView.All)}
              </Typography>
              {tags && tags.length > 0 && (
                <TagsDisplay
                  tags={tags?.map((tag) => ({ id: tag, name: tag })) ?? []}
                />
              )}
            </Stack>
            <IconButton onClick={onClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        {/* Facility Details */}
        <Typography variant="caption" color="text.secondary">
          {facility?.name}{" "}
          {facility?.customerFacilityId
            ? `(${facility?.customerFacilityId}) `
            : ""}
          - EPA ID: {epaId} -{" "}
          {formatAddress(
            facility?.streetAddress1,
            facility?.city,
            facility?.state,
            facility?.zip
          )}
        </Typography>
      </Box>

      <Box sx={{ px: 3, pt: 1 }}>
        {/* Manifest Type Toggle */}
        <ToggleButtonGroup
          value={manifestType}
          exclusive
          onChange={handleManifestToggle}
          sx={{ mb: 2 }}
          color="primary"
        >
          <ToggleButton sx={{ p: 1 }} value="vendor">
            Vendor
          </ToggleButton>
          <ToggleButton sx={{ p: 1 }} value="e‑Manifest">
            e‑Manifest
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Content */}
      <Stack sx={{ height: "100%" }} gap={3} px={3} py={2}>
        {/* Show placeholder if no data for selected manifest type */}
        {hasNoManifestData ? (
          <>
            <TileContentGreyBackground>{`${
              manifestType === "vendor" ? "🚚 No vendor" : "🗂️ No e‑Manifest"
            } data to show`}</TileContentGreyBackground>
          </>
        ) : (
          <>
            {/* Show data based on selected manifest type */}
            <WasteStreamInfoSection
              wasteStreamId={wasteStreamId}
              manifestType={manifestType}
              facilityState={facility?.state || undefined}
            />

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                alignSelf: "stretch",
                width: "100%",
              }}
            >
              <WasteStreamShipmentsByMonth
                wasteStreamId={wasteStreamId}
                startDate={startDate ?? ""}
                endDate={endDate ?? ""}
                manifestType={manifestType}
              />
            </Box>
          </>
        )}

        <Divider />

        <Box>
          <DestinationsDataGrid wasteStreamId={wasteStreamId} />
        </Box>

        <ManifestLineItemsDataGrid wasteStreamId={wasteStreamId} />
      </Stack>

      {/* Edit Dialog */}
      {editDialogOpen && (
        <EditWasteStreamDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          wasteStreamId={wasteStreamId}
          hasVendorData={manifestState.hasVendorData}
        />
      )}

      {/* Unmerge Dialog for merged waste streams */}
      {unmergeDialogOpen && wasteStreamData?.wasteStream && (
        <WasteStreamUnmergeDialog
          onClose={() => setUnmergeDialogOpen(false)}
          wasteStream={wasteStreamData.wasteStream}
        />
      )}
    </>
  );
};

export const WasteStreamDetailsModal = ({
  open,
  onClose,
  wasteStreamId,
}: WasteStreamDetailsModalProps) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: 1350,
          maxWidth: "80%",
        },
      }}
    >
      {open && (
        <Suspense fallback={<WasteStreamDetailsModalSkeleton />}>
          <WasteStreamDetailsContent
            wasteStreamId={wasteStreamId}
            onClose={onClose}
          />
        </Suspense>
      )}
    </Drawer>
  );
};

const WasteStreamDetailTitle = ({
  wasteStreamId,
  onEditClick,
}: {
  wasteStreamId: string;
  onEditClick: () => void;
}) => {
  const { data: wasteStreamData } = useWasteStream(wasteStreamId);
  const { isStaff } = useCurrentUser();
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
      <Typography variant="h5" sx={{ wordBreak: "break-word", m: 0, p: 0 }}>
        {wasteStreamData?.wasteStream?.name || "Waste Stream"}
      </Typography>
      {isStaff && (
        <MergedWasteStreamChip
          mergeCount={wasteStreamData?.wasteStream?.mergeCount ?? 0}
          sx={{ mt: 0.5 }}
        />
      )}
      {/* Edit Button for Staff */}
      {isStaff && (
        <IconButton
          size="small"
          onClick={onEditClick}
          sx={{
            color: "warning.main",
            flexShrink: 0,
            alignSelf: "flex-start",
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};
